import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import PaymentForm from '../../components/PaymentForm';
import StripeConnect from '../../components/StripeConnect';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-hot-toast';
import apiClient from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable is not set');
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Removed mock useAuth - now using the real AuthContext

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`,
  };
}

const MyAccountPage = () => {
  const { user, getPaymentMethods, currentAccountType } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Account data state
  const [accountData, setAccountData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState();
  const [company, setCompany] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [updateSubscriptionModalOpen, setUpdateSubscriptionModalOpen] = useState(false);
  const [editingBranches, setEditingBranches] = useState(1);
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [cancelSubscriptionConfirmOpen, setCancelSubscriptionConfirmOpen] = useState(false);
  const [branchesCount, setBranchesCount] = useState(0);
  
  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: ''
  });


  const [addressInfo, setAddressInfo] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    weeklyDigest: true
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Logo upload state
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deleteLogoDialogOpen, setDeleteLogoDialogOpen] = useState(false);
  const logoInputRef = useRef(null);
  
  const handleTabChange = (event, newValue) => {
    // Staff users should only see tab 0 (My Info)
    if (currentAccountType === 'staff' && newValue !== 0) {
      setTabValue(0);
    } else {
      setTabValue(newValue);
    }
  };
  
  // Reset tab to 0 if staff user somehow gets to a hidden tab
  useEffect(() => {
    if (currentAccountType === 'staff' && tabValue !== 0) {
      setTabValue(0);
    }
  }, [currentAccountType, tabValue]);

  // Load company data when Payment & Billing tab is accessed
  useEffect(() => {
    if (tabValue === 1 && currentAccountType !== 'staff' && user?.companyId) {
      console.log('Payment & Billing tab accessed, loading company data');
      loadCompanyData();
    }
  }, [tabValue, currentAccountType, user?.companyId]);

  useEffect(() => {
    loadAccountData();
    // Only load payment methods for non-staff users
    if (currentAccountType !== 'staff') {
      refreshPaymentMethods();
      // Load company/subscription data if user has a companyId
      if (user?.companyId) {
        console.log('Calling loadCompanyData for companyId:', user.companyId);
        loadCompanyData();
      } else {
        console.log('Not calling loadCompanyData - no companyId. User:', user);
      }
    } else {
      console.log('Not calling loadCompanyData - staff account type');
    }
  }, [user, currentAccountType]);

  const loadAccountData = async () => {
    try {
      setIsLoading(true);
      
      // Load user profile from API
      const response = await apiClient.getProfile();
      console.log('Profile API response:', response);
      
      // API returns { user: {...} }, and apiClient.getProfile() returns response.data
      // So response is { user: {...} }
      const accountData = response?.user || response;
      
      setAccountData(accountData);
      
      // Set form data with safe property access
      setPersonalInfo({
        firstName: accountData?.firstName || '',
        lastName: accountData?.lastName || '',
        email: accountData?.email || '',
        phone: accountData?.phone || '',
        company: accountData?.company || ''
      });
      
      setAddressInfo({
        street: accountData?.address?.street || '',
        city: accountData?.address?.city || '',
        state: accountData?.address?.state || '',
        zipCode: accountData?.address?.zipCode || '',
        country: accountData?.address?.country || ''
      });
      
      setPreferences(accountData?.preferences || {
        emailNotifications: true,
        smsNotifications: false,
        marketingEmails: false,
        weeklyDigest: true
      });

      // Load logo if available (check for logoId and generate URL from API endpoint)
      if (accountData?.logoId && accountData?.id) {
        // Generate logo URL from API endpoint (similar to card images)
        const apiBaseUrl = apiClient.client.defaults.baseURL || 'http://localhost:8000';
        setLogoUrl(`${apiBaseUrl}/api/users/${accountData.id}/logo`);
      }
      
    } catch (error) {
      console.error('Error loading account data:', error);
      toast.error('Failed to load account data');
      
      // Set default values on error
      setPersonalInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: ''
      });
      
      setAddressInfo({
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      });
      
      setPreferences({
        emailNotifications: true,
        smsNotifications: false,
        marketingEmails: false,
        weeklyDigest: true
      });
    } finally {
      setIsLoading(false);
    }
  };


  const refreshPaymentMethods = async () => {
    try {
      const methods = await getPaymentMethods();
      console.log('methods', methods);
      setPaymentMethod(methods[0]);
    } catch (e) {
      console.error('Error fetching payment methods:', e);
    }
  };

  const loadCompanyData = async () => {
    if (!user?.companyId) {
      console.log('loadCompanyData: No companyId, returning early');
      return;
    }
    
    console.log('loadCompanyData: Starting to load company data for companyId:', user.companyId);
    try {
      setSubscriptionLoading(true);
      const response = await apiClient.getFuneralCompany(user.companyId);
      console.log('loadCompanyData: Company response:', response);
      const companyData = response.company || response.data || response;
      console.log('loadCompanyData: Setting company data:', companyData);
      setCompany(companyData);
      
      // Load branches count
      try {
        const branchesResponse = await apiClient.getBranches({ 
          companyId: user.companyId, 
          page: 1, 
          limit: 1 
        });
        console.log('Branches response for count:', branchesResponse);
        // Use pagination.total for the count
        const count = branchesResponse.pagination?.total || 0;
        console.log('Setting branches count to:', count);
        setBranchesCount(count);
      } catch (error) {
        console.error('Error loading branches count:', error);
        setBranchesCount(0);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      // Don't show error toast - subscription might not exist yet
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Helper function to format subscription plan
  const formatSubscriptionPlan = (plan) => {
    if (!plan) return 'No subscription';
    return plan === 'monthly' ? 'Monthly' : 'Annual';
  };

  // Helper function to calculate subscription amount
  const getSubscriptionAmount = () => {
    if (!company?.subscriptionPlan || !company?.numberOfBranches) return null;
    const baseAmount = company.subscriptionPlan === 'monthly' ? 50 : 420;
    return baseAmount * company.numberOfBranches;
  };

  // Helper function to format subscription status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
      case 'unpaid':
        return 'warning';
      case 'canceled':
      case 'incomplete':
      case 'incomplete_expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleUpdateSubscription = async () => {
    if (!user?.companyId || !editingBranches || editingBranches < 1) {
      toast.error('Please enter a valid number of branches');
      return;
    }

    if (editingBranches < branchesCount) {
      toast.error(`You cannot reduce below ${branchesCount} ${branchesCount === 1 ? 'branch' : 'branches'}. You have ${branchesCount} ${branchesCount === 1 ? 'branch' : 'branches'} already created.`);
      return;
    }

    try {
      setIsUpdatingSubscription(true);
      const response = await apiClient.updateSubscription({
        numberOfBranches: editingBranches,
      });

      // Reload company data to get updated subscription info
      await loadCompanyData();

      setUpdateSubscriptionModalOpen(false);
      setEditingBranches(company?.numberOfBranches || 1);
      toast.success('Subscription updated successfully');
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to update subscription');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!company?.subscriptionId) {
      toast.error('No active subscription found');
      return;
    }

    try {
      setIsCancellingSubscription(true);
      await apiClient.updateSubscription({ action: 'cancel' });
      await loadCompanyData();
      setCancelSubscriptionConfirmOpen(false);
      setUpdateSubscriptionModalOpen(false);
      toast.success('Subscription will be canceled at the end of your billing period');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to cancel subscription');
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleRenewSubscription = async () => {
    if (!company?.subscriptionId) {
      toast.error('No subscription found');
      return;
    }

    try {
      setIsCancellingSubscription(true);
      await apiClient.updateSubscription({ action: 'renew' });
      await loadCompanyData();
      setUpdateSubscriptionModalOpen(false);
      toast.success('Subscription renewed successfully');
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to renew subscription');
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setIsSaving(true);
      
      // Update profile via API
      await apiClient.updateProfile({
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        phone: personalInfo.phone,
        company: personalInfo.company
      });
      
      // Update address via API
      await apiClient.updateUser(user.id, { address: addressInfo });
      
      // Update local state
      if (accountData) {
        const updatedAccount = {
          ...accountData,
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          phone: personalInfo.phone,
          company: personalInfo.company,
          address: addressInfo
        };
        setAccountData(updatedAccount);
      }
      
      toast.success('Account information updated successfully');
    } catch (error) {
      console.error('Error updating account info:', error);
      toast.error('Failed to update account information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMethodUpdated = async () => {
    refreshPaymentMethods();
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingLogo(true);
      
      const response = await apiClient.uploadDesignerLogo(file);
      
      if (response.success && response.data?.logoId && user?.id) {
        // Generate logo URL from API endpoint (similar to card images)
        const apiBaseUrl = apiClient.client.defaults.baseURL || 'http://localhost:8000';
        const logoUrl = `${apiBaseUrl}/api/users/${user.id}/logo`;
        setLogoUrl(logoUrl);
        // Update accountData with new logoId
        if (accountData) {
          setAccountData({
            ...accountData,
            logoId: response.data.logoId
          });
        }
        toast.success('Logo uploaded successfully');
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current?.click();
  };

  const handleDeleteLogo = async () => {
    if (!user?.id) return;
    
    try {
      const response = await apiClient.deleteDesignerLogo();
      
      if (response.success) {
        setLogoUrl(null);
        // Update accountData to remove logoId
        if (accountData) {
          const { logoId: _, ...updatedAccount } = accountData;
          setAccountData(updatedAccount);
        }
        toast.success('Logo removed successfully');
        setDeleteLogoDialogOpen(false);
      } else {
        throw new Error(response.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to delete logo');
    }
  };


  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <DashboardLayout title="My Account">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            My Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account information, preferences, and billing details
          </Typography>
        </Box>         

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="account tabs">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  My Info
                </Box>
              } 
              {...a11yProps(0)} 
            />
            {currentAccountType !== 'staff' && (
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon fontSize="small" />
                    Payment & Billing
                  </Box>
                } 
                {...a11yProps(1)} 
              />
            )}
            {currentAccountType !== 'staff' && (
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon fontSize="small" />
                    Stripe Connect
                  </Box>
                } 
                {...a11yProps(2)} 
              />
            )}
          </Tabs>
        </Box>

        {/* My Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      Personal Information
                    </Box>
                  }
                  subheader="Update your personal details"
                  sx={{ 
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 }
                  }}
                />
                <CardContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={personalInfo.firstName}
                      onChange={(e) => setPersonalInfo(prev => ({ ...prev, firstName: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={personalInfo.lastName}
                      onChange={(e) => setPersonalInfo(prev => ({ ...prev, lastName: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={personalInfo.email}
                      onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone"
                      value={personalInfo.phone}
                      onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Company"
                      value={personalInfo.company}
                      onChange={(e) => setPersonalInfo(prev => ({ ...prev, company: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <CreditCardIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon />
                      Address Information
                    </Box>
                  }
                  subheader="Update your address details"
                  sx={{ 
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 }
                  }}
                />
                <CardContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={addressInfo.street}
                      onChange={(e) => setAddressInfo(prev => ({ ...prev, street: e.target.value }))}
                      size="medium"
                      InputProps={{
                        startAdornment: <LocationIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      }}
                    />
                    <TextField
                      fullWidth
                      label="City"
                      value={addressInfo.city}
                      onChange={(e) => setAddressInfo(prev => ({ ...prev, city: e.target.value }))}
                      size="medium"
                    />
                    <TextField
                      fullWidth
                      label="State/Province"
                      value={addressInfo.state}
                      onChange={(e) => setAddressInfo(prev => ({ ...prev, state: e.target.value }))}
                      size="medium"
                    />
                    <TextField
                      fullWidth
                      label="ZIP/Postal Code"
                      value={addressInfo.zipCode}
                      onChange={(e) => setAddressInfo(prev => ({ ...prev, zipCode: e.target.value }))}
                      size="medium"
                    />
                    <TextField
                      fullWidth
                      label="Country"
                      value={addressInfo.country}
                      onChange={(e) => setAddressInfo(prev => ({ ...prev, country: e.target.value }))}
                      size="medium"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                variant="contained"
                onClick={handleSaveAll}
                disabled={isSaving}
                startIcon={<SaveIcon />}
                size="large"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  py: { xs: 1.5, sm: 1.25 }
                }}
              >
                {isSaving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </Box>

            {/* Logo Upload Section - Only for Designers */}
            {(currentAccountType === 'designer' || user?.type === 'designer') && (
              <Box>
                <Card>
                  <CardHeader
                    title="Logo"
                    subheader="Upload a logo that will appear on the back of all your cards"
                    action={
                      logoUrl ? (
                        <Tooltip title="Delete Logo">
                          <IconButton
                            color="error"
                            onClick={() => setDeleteLogoDialogOpen(true)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <CardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      {logoUrl ? (
                        <Box sx={{ 
                          width: { xs: '100%', sm: 200 }, 
                          maxWidth: { xs: '100%', sm: 200 },
                          minHeight: 200,
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          boxShadow: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <img
                            src={logoUrl}
                            alt="Designer Logo"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              height: 'auto',
                              width: 'auto',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      ) : (
                        <>
                          <input
                            type="file"
                            ref={logoInputRef}
                            onChange={handleLogoUpload}
                            accept="image/*"
                            style={{ display: 'none' }}
                          />
                          <Tooltip title="Click to upload logo" placement="top">
                            <Box 
                              onClick={handleLogoUploadClick}
                              sx={{ 
                                width: { xs: '100%', sm: 200 }, 
                                height: { xs: 150, sm: 200 },
                                border: '2px dashed',
                                borderColor: 'primary.main',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'primary.50',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'primary.100',
                                  borderColor: 'primary.dark',
                                  transform: 'scale(1.02)'
                                }
                              }}
                            >
                              {uploadingLogo ? (
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center', 
                                  gap: 2,
                                  p: 3
                                }}>
                                  <CircularProgress size={40} />
                                  <Typography variant="body2" color="text.secondary" align="center">
                                    Uploading...
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  p: 2,
                                  gap: 1
                                }}>
                                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.6 }} />
                                  <Typography variant="body2" color="primary.main">
                                    No logo uploaded
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Click here to upload logo
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Payment & Billing Tab - Hidden for staff users */}
        {currentAccountType !== 'staff' && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Subscription Details */}
              {user?.companyId && (
                <Card>
                  <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CreditCardIcon />
                          Subscription Details
                        </Box>
                      }
                      subheader="View your current subscription information"
                      action={
                        company?.subscriptionId || company?.subscriptionPlan ? (
                          company?.subscriptionCancelAtPeriodEnd ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={handleRenewSubscription}
                              disabled={isCancellingSubscription}
                            >
                              {isCancellingSubscription ? 'Renewing...' : 'Renew Subscription'}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setEditingBranches(company.numberOfBranches || 1);
                                setUpdateSubscriptionModalOpen(true);
                              }}
                              disabled={isCancellingSubscription}
                            >
                              Update Subscription
                            </Button>
                          )
                        ) : null
                      }
                    />
                    <CardContent>
                      {subscriptionLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : company?.subscriptionId || company?.subscriptionPlan ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {/* Branches Remaining Alert */}
                          {company.numberOfBranches && (
                            (() => {
                              const remaining = Math.max(0, company.numberOfBranches - branchesCount);
                              const hasNoBranchesLeft = remaining <= 0;
                              
                              return (
                                <Alert 
                                  severity={hasNoBranchesLeft ? "warning" : "info"}
                                  icon={hasNoBranchesLeft ? <WarningIcon /> : <InfoIcon />}
                                >
                                  {hasNoBranchesLeft ? (
                                    <>
                                      You've used all {company.numberOfBranches} {company.numberOfBranches === 1 ? 'branch' : 'branches'} in your subscription. Update your subscription to add more branches.
                                    </>
                                  ) : (
                                    <>
                                      You have {remaining} of {company.numberOfBranches} {company.numberOfBranches === 1 ? 'branch' : 'branches'} remaining ({branchesCount} {branchesCount === 1 ? 'branch' : 'branches'} created).
                                    </>
                                  )}
                                </Alert>
                              );
                            })()
                          )}
                          
                          {/* Subscription Status */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Status
                            </Typography>
                            <Chip
                              label={company.subscriptionStatus || 'Unknown'}
                              color={getStatusColor(company.subscriptionStatus)}
                              size="small"
                              icon={company.subscriptionStatus === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                            />
                          </Box>
                          {company.subscriptionCancelAtPeriodEnd && (
                            <Alert severity="warning" icon={<WarningIcon />}>
                              Your subscription is set to cancel at the end of the current billing period.
                            </Alert>
                          )}

                          {/* Subscription Plan */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Plan
                            </Typography>
                            <Typography variant="body1">
                              {formatSubscriptionPlan(company.subscriptionPlan)}
                            </Typography>
                          </Box>

                          {/* Number of Branches */}
                          {company.numberOfBranches && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Number of Branches
                              </Typography>
                              <Typography variant="body1">
                                {company.numberOfBranches} {company.numberOfBranches === 1 ? 'branch' : 'branches'}
                              </Typography>
                            </Box>
                          )}

                          {/* Subscription Amount */}
                          {getSubscriptionAmount() && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Amount
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                ${getSubscriptionAmount()}
                                {company.subscriptionPlan === 'monthly' ? '/month' : '/year'}
                              </Typography>
                            </Box>
                          )}

                          {/* Subscription ID */}
                          {company.subscriptionId && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant="body2" color="text.secondary">
                                Subscription ID
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {company.subscriptionId}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No active subscription found.
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Complete the account setup to create a subscription.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
              )}

              {/* Stripe Connect Account Status */}
              <Card>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon />
                        Credit Card Information
                      </Box>
                    }
                    subheader="Manage your payment method for subscriptions"
                  />
                  <CardContent>
                    <Elements stripe={stripePromise}>
                      <PaymentForm paymentMethod={paymentMethod} methodUpdated={handleMethodUpdated} />
                    </Elements>
                  </CardContent>
                </Card>
            </Box>
          </TabPanel>
        )}

        {/* Stripe Connect Tab - Hidden for staff users */}
        {currentAccountType !== 'staff' && (
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StripeConnect showAsCard={true} showTitle={true} />
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Box>

      {/* Delete Logo Confirmation Dialog */}
      <Dialog
        open={deleteLogoDialogOpen}
        onClose={() => setDeleteLogoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Logo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your logo? This will remove it from all your cards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteLogoDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLogo}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Logo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Subscription Modal */}
      <Dialog
        open={updateSubscriptionModalOpen}
        onClose={() => !isUpdatingSubscription && !isCancellingSubscription && setUpdateSubscriptionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update the number of branches in your subscription. The subscription amount will be recalculated and prorated.
            </Typography>
            
            {company && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Subscription
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {company.numberOfBranches} {company.numberOfBranches === 1 ? 'branch' : 'branches'} - {formatSubscriptionPlan(company.subscriptionPlan)}
                </Typography>
                {getSubscriptionAmount() && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    ${getSubscriptionAmount()}{company.subscriptionPlan === 'monthly' ? '/month' : '/year'}
                  </Typography>
                )}
                {branchesCount > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {branchesCount} {branchesCount === 1 ? 'branch' : 'branches'} already created (minimum required)
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Number of Branches"
              type="number"
              value={editingBranches}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1) {
                  setEditingBranches(value);
                } else if (e.target.value === '') {
                  setEditingBranches('');
                }
              }}
              inputProps={{ min: branchesCount || 1, step: 1 }}
              required
              helperText={
                editingBranches && editingBranches < branchesCount
                  ? `You cannot reduce below ${branchesCount} ${branchesCount === 1 ? 'branch' : 'branches'} (you have ${branchesCount} ${branchesCount === 1 ? 'branch' : 'branches'} created)`
                  : `Enter the number of branches you need (minimum: ${branchesCount || 1})`
              }
              error={!editingBranches || editingBranches < 1 || (editingBranches < branchesCount)}
            />

            {editingBranches && editingBranches >= 1 && company?.subscriptionPlan && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    New Subscription Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    ${(() => {
                      const baseAmount = company.subscriptionPlan === 'monthly' ? 50 : 420;
                      return baseAmount * editingBranches;
                    })()}{company.subscriptionPlan === 'monthly' ? '/month' : '/year'}
                  </Typography>
                  {editingBranches !== company.numberOfBranches && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {editingBranches > company.numberOfBranches 
                        ? `Increase of ${editingBranches - company.numberOfBranches} ${editingBranches - company.numberOfBranches === 1 ? 'branch' : 'branches'}`
                        : `Decrease of ${company.numberOfBranches - editingBranches} ${company.numberOfBranches - editingBranches === 1 ? 'branch' : 'branches'}`
                      }
                    </Typography>
                  )}
                </Box>
                
                {editingBranches > company.numberOfBranches && (
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Added Charge: ${(() => {
                        const baseAmount = company.subscriptionPlan === 'monthly' ? 50 : 420;
                        const additionalBranches = editingBranches - company.numberOfBranches;
                        return baseAmount * additionalBranches;
                      })()}
                    </Typography>
                    <Typography variant="body2">
                      You will be charged immediately for {editingBranches - company.numberOfBranches} additional {editingBranches - company.numberOfBranches === 1 ? 'branch' : 'branches'}. The charge will be prorated based on the remaining days in your current billing cycle. The exact prorated amount will be calculated by Stripe and shown on your invoice.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          {company?.subscriptionCancelAtPeriodEnd ? (
            <Button
              onClick={handleRenewSubscription}
              color="success"
              disabled={isUpdatingSubscription || isCancellingSubscription}
            >
              {isCancellingSubscription ? 'Renewing...' : 'Renew Subscription'}
            </Button>
          ) : (
            <Button
              onClick={() => setCancelSubscriptionConfirmOpen(true)}
              color="error"
              disabled={isUpdatingSubscription || isCancellingSubscription}
            >
              {isCancellingSubscription ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              setUpdateSubscriptionModalOpen(false);
              setEditingBranches(company?.numberOfBranches || 1);
            }}
            disabled={isUpdatingSubscription || isCancellingSubscription}
          >
            Cancel
          </Button>
            <Button
              onClick={handleUpdateSubscription}
              variant="contained"
              disabled={isUpdatingSubscription || isCancellingSubscription || !editingBranches || editingBranches < 1 || editingBranches < branchesCount || editingBranches === company?.numberOfBranches}
            >
              {isUpdatingSubscription ? 'Updating...' : 'Update Subscription'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelSubscriptionConfirmOpen}
        onClose={() => !isCancellingSubscription && setCancelSubscriptionConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Subscription Cancellation</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Canceling your subscription will prevent you from generating any additional revenue/income from the card store.
          </Alert>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Your subscription will remain active until the end of your current billing period, then it will be canceled.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After cancellation takes effect, you will not be able to continue earning from new card store sales.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            variant="outlined"
            disabled={isCancellingSubscription}
          >
            {isCancellingSubscription ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
          <Button
            onClick={() => setCancelSubscriptionConfirmOpen(false)}
            variant="contained"
            disabled={isCancellingSubscription}
          >
            Keep Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default MyAccountPage;
